// Admin data client. All reads/writes go through the serverless functions in
// api/, which hold the Upstash token server-side. Writes are authenticated with
// the ADMIN_API_TOKEN (a shared secret the admin enters once), sent as a bearer
// token — the browser never handles the database credential. See docs/AUDIT.md.

import { API_BASE } from '../config/api';

// --- Admin token (stored in the browser so it isn't re-entered each visit) ---
export function getStoredAdminToken() {
  try {
    return localStorage.getItem('hchs_admin_token') || '';
  } catch (_) {
    return '';
  }
}

export function setStoredAdminToken(token) {
  try {
    localStorage.setItem('hchs_admin_token', token || '');
  } catch (_) {
    // ignore
  }
}

function authHeaders(token) {
  const t = token || getStoredAdminToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

// Confirm the admin token is valid by making a harmless authenticated request.
// api/event-admin authenticates first (401 on a bad token); with no capacity/sold
// in the body it writes nothing and returns 200.
export async function validateAdminToken(token) {
  try {
    if (!token) return false;
    const resp = await fetch(`${API_BASE}/api/event-admin`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders(token) },
      body: JSON.stringify({ slug: '__validate__' }),
    });
    return resp.ok;
  } catch (_) {
    return false;
  }
}

// --- Event status (reads are public; writes need the admin token) ---
export async function getEventStatus(slug) {
  const resp = await fetch(`${API_BASE}/api/event-status?slug=${encodeURIComponent(slug)}`);
  if (!resp.ok) throw new Error('Failed to load event status');
  const data = await resp.json().catch(() => null);
  if (!data || data.error) throw new Error((data && data.error) || 'Failed to load event status');
  return data; // { slug, capacity, sold, remaining }
}

export async function setEventStatus({ slug, capacity, sold }) {
  const body = { slug };
  if (Number.isFinite(Number(capacity))) body.capacity = Number(capacity);
  if (Number.isFinite(Number(sold))) body.sold = Number(sold);
  const resp = await fetch(`${API_BASE}/api/event-admin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify(body),
  });
  if (resp.status === 401) throw new Error('Unauthorized — check the admin token');
  if (!resp.ok) {
    const t = await resp.text().catch(() => '');
    throw new Error(t || 'Failed to save event status');
  }
  return resp.json();
}

// --- Members list ---
export async function fetchMembersText() {
  const resp = await fetch(`${API_BASE}/api/members`);
  if (!resp.ok) throw new Error('Failed to load members');
  return String((await resp.text()) || '');
}

export async function saveMembersText(text) {
  const resp = await fetch(`${API_BASE}/api/members-admin`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({ text: String(text || '') }),
  });
  if (resp.status === 401) throw new Error('Unauthorized — check the admin token');
  if (!resp.ok) {
    const t = await resp.text().catch(() => '');
    throw new Error(t || 'Failed to save members');
  }
  return { ok: true };
}
